<template>
  <a-card :bordered="false">
    <a-alert
      type="warning"
      show-icon
      style="margin-bottom: 16px"
      message="服务器异常导致考勤 Redis 卡住时，在本页扫描并修复。不会改打卡/收口逻辑：先把已下班数据写入 MySQL，再清掉卡住的日期标记，下一包手环会按原流程重建。"
    />

    <a-tabs v-model:activeKey="activeKey" @change="onTabChange">
      <a-tab-pane key="diagnose" tab="异常诊断">
        <div class="table-operations">
          <a-checkbox v-model:checked="onlyAbnormal">仅显示异常</a-checkbox>
          <a-checkbox v-model:checked="forceCurrent">强制重置当前班次</a-checkbox>
          <a-button type="primary" :loading="diagnoseLoading" @click="loadDiagnose">扫描</a-button>
          <a-button
            :disabled="!selectedRowKeys.length"
            :loading="repairLoading"
            v-hasPermi="['system:attendanceRedisRepair:repair']"
            @click="handleRepairSelected"
          >
            修复选中
          </a-button>
          <a-button
            :loading="repairLoading"
            v-hasPermi="['system:attendanceRedisRepair:repair']"
            @click="handleRepairAll"
          >
            修复全部异常
          </a-button>
        </div>

        <a-table
          :loading="diagnoseLoading"
          rowKey="attendanceId"
          :columns="diagnoseColumns"
          :data-source="diagnoseList"
          :row-selection="{ selectedRowKeys, onChange: onSelectChange }"
          :pagination="false"
          :scroll="{ x: 1400 }"
          :locale="{ emptyText: '未发现异常，或请先取消「仅显示异常」再扫描' }"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.dataIndex === 'shiftTime'">
              {{ (record.startTime || '-') + ' ~ ' + (record.endTime || '-') }}
            </template>
            <template v-else-if="column.dataIndex === 'isOvernight'">
              {{ record.isOvernight === '1' ? '是' : '否' }}
            </template>
            <template v-else-if="column.dataIndex === 'abnormal'">
              <a-tag v-if="record.abnormal" color="red">异常</a-tag>
              <a-tag v-else color="green">正常</a-tag>
            </template>
            <template v-else-if="column.dataIndex === 'zsetCounts'">
              <span>{{ (record.zsetCounts || []).join('，') || '-' }}</span>
            </template>
            <template v-else-if="column.dataIndex === 'operation'">
              <a
                v-hasPermi="['system:attendanceRedisRepair:repair']"
                @click="handleRepairOne(record)"
              >修复</a>
            </template>
          </template>
        </a-table>
      </a-tab-pane>

      <a-tab-pane key="log" tab="修复记录">
        <div class="table-page-search-wrapper">
          <a-form layout="inline">
            <a-form-item label="规则名称">
              <a-input v-model:value="logQuery.attendanceName" placeholder="请输入" allow-clear />
            </a-form-item>
            <a-form-item label="项目名称">
              <a-input v-model:value="logQuery.projectName" placeholder="请输入" allow-clear />
            </a-form-item>
            <a-form-item label="状态">
              <a-select v-model:value="logQuery.status" placeholder="全部" allow-clear style="width: 120px">
                <a-select-option value="success">成功</a-select-option>
                <a-select-option value="fail">失败</a-select-option>
              </a-select>
            </a-form-item>
            <a-form-item>
              <a-button type="primary" @click="loadLog">查询</a-button>
              <a-button style="margin-left: 8px" @click="resetLogQuery">重置</a-button>
            </a-form-item>
          </a-form>
        </div>

        <a-table
          :loading="logLoading"
          rowKey="id"
          :columns="logColumns"
          :data-source="logList"
          :pagination="false"
          :scroll="{ x: 1600 }"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.dataIndex === 'status'">
              <a-tag v-if="record.status === 'success'" color="green">成功</a-tag>
              <a-tag v-else color="red">失败</a-tag>
            </template>
            <template v-else-if="column.dataIndex === 'forceCurrent'">
              {{ record.forceCurrent === 1 ? '是' : '否' }}
            </template>
            <template v-else-if="column.dataIndex === 'createTime'">
              {{ parseTime(record.createTime) }}
            </template>
            <template v-else-if="column.dataIndex === 'operation'">
              <a @click="showLogDetail(record)">查看落库人员</a>
            </template>
          </template>
        </a-table>
        <a-pagination
          class="ant-table-pagination"
          show-size-changer
          show-quick-jumper
          :current="logQuery.pageNum"
          :total="logTotal"
          :page-size="logQuery.pageSize"
          :showTotal="(total) => `共 ${total} 条`"
          @showSizeChange="onLogSizeChange"
          @change="onLogPageChange"
        />
      </a-tab-pane>
    </a-tabs>

    <a-modal v-model:visible="detailVisible" title="落库人员" :footer="null" width="640px">
      <p style="margin-bottom: 8px">{{ detailRecord.message }}</p>
      <a-textarea :value="detailMembers" :rows="12" readonly />
    </a-modal>
  </a-card>
</template>

<script>
import { Modal } from 'ant-design-vue'
import {
  diagnoseAttendanceRedis,
  repairAttendanceRedis,
  repairBatchAttendanceRedis,
  pageAttendanceRedisRepairLog,
} from '@/api/system/attendanceRedisRepair'
import { parseTime } from '@/utils/ruoyi'

export default {
  name: 'AttendanceRedisRepair',
  data() {
    return {
      activeKey: 'diagnose',
      onlyAbnormal: true,
      forceCurrent: false,
      diagnoseLoading: false,
      repairLoading: false,
      diagnoseList: [],
      selectedRowKeys: [],
      diagnoseColumns: [
        { title: '规则名称', dataIndex: 'attendanceName', width: 160, ellipsis: true },
        { title: '项目', dataIndex: 'projectName', width: 160, ellipsis: true },
        { title: '班次', dataIndex: 'shiftTime', width: 160 },
        { title: '跨天', dataIndex: 'isOvernight', width: 70 },
        { title: 'Redis日期标记', dataIndex: 'stickyDate', width: 130 },
        { title: '当前班次日期', dataIndex: 'currentOpenDate', width: 130 },
        { title: 'Redis打卡', dataIndex: 'zsetCounts', width: 180 },
        { title: '人数', dataIndex: 'zsetMemberCount', width: 70 },
        { title: '手环缓存', dataIndex: 'hashCount', width: 90 },
        { title: '状态', dataIndex: 'abnormal', width: 80 },
        { title: '说明', dataIndex: 'issueText', width: 280, ellipsis: true },
        { title: '操作', dataIndex: 'operation', width: 80, fixed: 'right' },
      ],
      logLoading: false,
      logList: [],
      logTotal: 0,
      logQuery: {
        pageNum: 1,
        pageSize: 10,
        attendanceName: undefined,
        projectName: undefined,
        status: undefined,
      },
      logColumns: [
        { title: '修复时间', dataIndex: 'createTime', width: 170 },
        { title: '规则名称', dataIndex: 'attendanceName', width: 160, ellipsis: true },
        { title: '项目', dataIndex: 'projectName', width: 160, ellipsis: true },
        { title: '修复前日期', dataIndex: 'stickyDate', width: 120 },
        { title: '落库日期', dataIndex: 'flushedDates', width: 180, ellipsis: true },
        { title: '落库人数', dataIndex: 'zsetMemberCount', width: 90 },
        { title: '清理缓存', dataIndex: 'hashClearedCount', width: 90 },
        { title: '强制当前班次', dataIndex: 'forceCurrent', width: 120 },
        { title: '状态', dataIndex: 'status', width: 80 },
        { title: '操作人', dataIndex: 'operatorName', width: 120 },
        { title: '说明', dataIndex: 'message', width: 280, ellipsis: true },
        { title: '操作', dataIndex: 'operation', width: 110, fixed: 'right' },
      ],
      detailVisible: false,
      detailRecord: {},
      detailMembers: '',
    }
  },
  created() {
    this.loadDiagnose()
  },
  methods: {
    parseTime,
    onTabChange(key) {
      if (key === 'log') {
        this.loadLog()
      } else {
        this.loadDiagnose()
      }
    },
    onSelectChange(keys) {
      this.selectedRowKeys = keys
    },
    loadDiagnose() {
      this.diagnoseLoading = true
      diagnoseAttendanceRedis(this.onlyAbnormal)
        .then((res) => {
          this.diagnoseList = res.data || []
          this.selectedRowKeys = []
        })
        .finally(() => {
          this.diagnoseLoading = false
        })
    },
    confirmText() {
      return this.forceCurrent
        ? '将把 Redis 中的考勤数据写入 MySQL，并清掉日期标记和手环缓存（含当前未下班班次）。下一包手环会重新激活。确认修复？'
        : '将把已下班的滞留 Redis 写入 MySQL，并清掉卡住的日期标记。当前未下班班次默认不动。确认修复？'
    },
    handleRepairOne(record) {
      Modal.confirm({
        title: `修复「${record.attendanceName || record.attendanceId}」`,
        content: this.confirmText(),
        onOk: () => this.doRepair({ attendanceId: record.attendanceId, forceCurrent: this.forceCurrent }),
      })
    },
    handleRepairSelected() {
      Modal.confirm({
        title: `修复选中的 ${this.selectedRowKeys.length} 条`,
        content: this.confirmText(),
        onOk: () =>
          this.doRepairBatch({
            attendanceIds: this.selectedRowKeys,
            forceCurrent: this.forceCurrent,
          }),
      })
    },
    handleRepairAll() {
      Modal.confirm({
        title: '修复全部异常',
        content: this.confirmText(),
        onOk: () => this.doRepairBatch({ forceCurrent: this.forceCurrent }),
      })
    },
    doRepair(payload) {
      this.repairLoading = true
      return repairAttendanceRedis(payload)
        .then((res) => {
          this.$message.success((res.data && res.data.message) || '修复完成')
          this.activeKey = 'log'
          this.loadLog()
          this.loadDiagnose()
        })
        .finally(() => {
          this.repairLoading = false
        })
    },
    doRepairBatch(payload) {
      this.repairLoading = true
      return repairBatchAttendanceRedis(payload)
        .then((res) => {
          const list = res.data || []
          const ok = list.filter((item) => item.status === 'success').length
          this.$message.success(`修复完成：成功 ${ok} / ${list.length}`)
          this.activeKey = 'log'
          this.loadLog()
          this.loadDiagnose()
        })
        .finally(() => {
          this.repairLoading = false
        })
    },
    loadLog() {
      this.logLoading = true
      pageAttendanceRedisRepairLog(this.logQuery)
        .then((res) => {
          this.logList = (res.data && res.data.records) || []
          this.logTotal = (res.data && res.data.total) || 0
        })
        .finally(() => {
          this.logLoading = false
        })
    },
    resetLogQuery() {
      this.logQuery = {
        pageNum: 1,
        pageSize: 10,
        attendanceName: undefined,
        projectName: undefined,
        status: undefined,
      }
      this.loadLog()
    },
    onLogPageChange(page, pageSize) {
      this.logQuery.pageNum = page
      this.logQuery.pageSize = pageSize
      this.loadLog()
    },
    onLogSizeChange(page, pageSize) {
      this.logQuery.pageNum = 1
      this.logQuery.pageSize = pageSize
      this.loadLog()
    },
    showLogDetail(record) {
      this.detailRecord = record || {}
      this.detailMembers = (record && record.memberSnapshot) || '无落库人员快照'
      this.detailVisible = true
    },
  },
  watch: {
    onlyAbnormal() {
      this.loadDiagnose()
    },
  },
}
</script>

<style scoped>
.table-operations {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.table-page-search-wrapper {
  margin-bottom: 16px;
}
</style>
